import React, { useState, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Upload, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const LENDER_STAGES = [
  'Pending',
  'Intimation Letter Sent',
  'Preferred Location Submitted',
  'Authority Letter Sent',
  'Legal Notice Reply Done',
  'Settlement Pending Approval',
  'Settlement Done',
];

const STAGE_COLORS = {
  'Pending': '#94a3b8',
  'Intimation Letter Sent': '#3b82f6',
  'Preferred Location Submitted': '#8b5cf6',
  'Authority Letter Sent': '#f59e0b',
  'Legal Notice Reply Done': '#10b981',
  'Settlement Pending Approval': '#f97316',
  'Settlement Done': '#16a34a',
};

function fmt(n) { return '₹' + (parseInt(n) || 0).toLocaleString('en-IN'); }

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function LenderStagesTab({ lead, onUpdate }) {
  const { user } = useAuth();
  const [updating, setUpdating] = useState(null);
  const [settlementModal, setSettlementModal] = useState(null);
  const isAdmin = user?.role === 'admin';
  const canUpdate = ['admin', 'tl', 'consultant'].includes(user?.role);

  const lenders = lead.proposalLenders || lead.creditAnalysis?.accounts?.filter(a => !a._excluded) || [];
  const lenderStages = lead.lenderStages || {};

  const updateStage = async (lenderName, stage) => {
    setUpdating(lenderName);
    try {
      if (stage === 'Settlement Pending Approval') {
        setSettlementModal(lenderName);
        setUpdating(null);
        return;
      }
      await api.patch(`/leads/${lead.id}/lender-stage`, { lenderName, stage });
      toast.success(`${lenderName}: Stage updated to "${stage}"`);
      onUpdate?.();
    } catch (e) {
      toast.error('Failed to update stage');
    } finally {
      setUpdating(null);
    }
  };

  if (lenders.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
        <AlertCircle size={32} style={{ marginBottom: 12 }} />
        <div style={{ fontWeight: 600, marginBottom: 8 }}>No lenders found</div>
        <div style={{ fontSize: 13 }}>Generate a proposal first to see lender-wise stages</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, padding: '8px 12px', background: 'var(--surface)', borderRadius: 8 }}>
        📋 Track settlement progress for each lender separately. Settlement requires Admin approval + PDF upload.
      </div>

      {lenders.map((lender, i) => {
        const lenderName = lender.lender || lender.name;
        const stageData = lenderStages[lenderName] || { stage: 'Pending' };
        const currentStage = stageData.stage || 'Pending';
        const isDone = currentStage === 'Settlement Done';
        const isPending = currentStage === 'Settlement Pending Approval';

        return (
          <div key={i} className="card mb-12" style={{
            borderLeft: `4px solid ${STAGE_COLORS[currentStage] || '#94a3b8'}`,
            opacity: isDone ? 0.85 : 1
          }}>
            <div className="flex-between" style={{ marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {lenderName}
                  {isDone && <span style={{ fontSize: 16 }}>✅</span>}
                  {isPending && <span style={{ fontSize: 16 }}>⏳</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 12 }}>
                  {lender.loan_type_label && <span>{lender.loan_type_label}</span>}
                  {lender.balance && <span>Outstanding: {fmt(lender.balance)}</span>}
                  {stageData.settlementAmount && <span style={{ color: 'var(--success)', fontWeight: 600 }}>Settlement: {fmt(stageData.settlementAmount)}</span>}
                </div>
              </div>
              <div style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: (STAGE_COLORS[currentStage] || '#94a3b8') + '20', color: STAGE_COLORS[currentStage] || '#94a3b8' }}>
                {currentStage}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ display: 'flex', gap: 3, marginBottom: 12 }}>
              {LENDER_STAGES.map((s, si) => {
                const stageIdx = LENDER_STAGES.indexOf(currentStage);
                const isActive = si <= stageIdx;
                return (
                  <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: isActive ? (STAGE_COLORS[s] || 'var(--navy)') : 'var(--border)', transition: 'background 0.3s' }} title={s} />
                );
              })}
            </div>

            {/* Stage selector */}
            {canUpdate && !isDone && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {LENDER_STAGES.filter(s => s !== 'Pending' && s !== currentStage).map(s => (
                  <button key={s}
                    disabled={updating === lenderName}
                    style={{
                      padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${STAGE_COLORS[s] || 'var(--border)'}`,
                      background: 'white', color: STAGE_COLORS[s] || 'var(--text-main)',
                      opacity: updating === lenderName ? 0.6 : 1
                    }}
                    onClick={() => updateStage(lenderName, s)}
                  >
                    {s === 'Settlement Pending Approval' ? '📄 Request Settlement' : s}
                  </button>
                ))}
              </div>
            )}

            {/* Stage history */}
            {stageData.updatedAt && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                Last updated: {new Date(stageData.updatedAt).toLocaleDateString('en-IN')} by {stageData.updatedBy}
                {stageData.notes && ` — ${stageData.notes}`}
              </div>
            )}

            {/* Settlement PDF link */}
            {stageData.settlementPdfFile && isAdmin && (
              <div style={{ marginTop: 8 }}>
                <a href={`${API_URL}/leads/settlement-pdf/${stageData.settlementPdfFile}`} target="_blank" rel="noreferrer"
                  className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>
                  📄 View Settlement PDF
                </a>
              </div>
            )}
          </div>
        );
      })}

      {/* Settlement Modal */}
      {settlementModal && (
        <SettlementRequestModal
          lead={lead}
          lenderName={settlementModal}
          onClose={() => setSettlementModal(null)}
          onSave={() => { setSettlementModal(null); onUpdate?.(); }}
        />
      )}
    </div>
  );
}

function SettlementRequestModal({ lead, lenderName, onClose, onSave }) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const save = async () => {
    if (!amount) return toast.error('Settlement amount required');
    if (!file) return toast.error('Settlement letter PDF required');
    setSaving(true);
    try {
      // Upload PDF first
      const fd = new FormData();
      fd.append('pdf', file);
      fd.append('lenderName', lenderName);
      await api.post(`/leads/${lead.id}/settlement-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      // Create settlement approval request
      await api.post(`/leads/${lead.id}/settlement-request`, {
        lenderName,
        settlementAmount: amount,
        notes
      });

      toast.success('Settlement approval request sent to Admin');
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit settlement request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">📄 Request Settlement Approval</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '8px 12px', background: '#fff7ed', borderRadius: 8, fontSize: 13, marginBottom: 16, color: '#92400e' }}>
            ⚠️ Admin approval is mandatory for settlement. Upload the settlement letter PDF received from the bank.
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>Lender: {lenderName}</div>

          <div className="form-group">
            <label className="form-label">Settlement Amount (₹) *</label>
            <input className="form-control" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 250000" />
          </div>

          <div className="form-group">
            <label className="form-label">Settlement Letter PDF * <span style={{ color: 'var(--danger)' }}>(From Bank)</span></label>
            <div
              style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '16px', textAlign: 'center', cursor: 'pointer', background: file ? '#f0fdf4' : 'var(--surface)' }}
              onClick={() => fileRef.current.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              {file ? (
                <div style={{ color: 'var(--success)', fontWeight: 600 }}>✓ {file.name}</div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Click to upload settlement letter PDF</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional details about settlement..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : '📤 Submit for Admin Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}
